import type React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { AddCircleOutlineOutlined, DeleteOutline } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import type { CrewEntry } from "./types/callsheet";
import { useState } from "react";

interface CrewTableProps {
  crewMembers: CrewEntry[];
  colors: any;
  reduxTheme: any;
  showAddButton: boolean;
  onAddCrewClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onDeleteCrew: (entryId: number) => void;
}

const CrewTable: React.FC<CrewTableProps> = ({
  crewMembers,
  colors,
  reduxTheme,
  showAddButton,
  onAddCrewClick,
  onMouseEnter,
  onMouseLeave,
  onDeleteCrew,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCrewId, setSelectedCrewId] = useState<number | null>(null);

  const handleConfirmDelete = () => {
    if (selectedCrewId !== null) {
      onDeleteCrew(selectedCrewId);
    }
    setOpenDialog(false);
    setSelectedCrewId(null);
  };

  const roleOrder = [
    "Executive Producer",
    "Producer",
    "Assistant Producer",
    "Liveswitcher",
    "Camera",
    "Networking",
    "Sound",
    "Tribute Table",
  ];

  const sortedCrewMembers = [...crewMembers].sort((a, b) => {
    const roleA = a.assignment?.[0]?.role || "N/A";
    const roleB = b.assignment?.[0]?.role || "N/A";

    const indexA = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleA.toLowerCase()
    );
    const indexB = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleB.toLowerCase()
    );

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  return (
    <>
      <Paper
        elevation={0}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          mt: 5,
          overflow: "hidden",
          "@media print": {
            margin: 0,
            border: "none",
            boxShadow: "none",
          },
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ background: reduxTheme.primary_colors[800] }}>
              <TableRow>
                {["Position", "Name", "Phone", "Email", "Actions"].map(
                  (header) => (
                    <TableCell
                      key={header}
                      sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <AnimatePresence>
                {sortedCrewMembers.map((entry, index) => {
                  const crewMember = entry.crew_member;
                  const user = crewMember.user;
                  const role = entry.assignment?.[0]?.role || "Unassigned";

                  const fullName =
                    `${user.first_name} ${user.last_name}`.trim();
                  const email = user.email || "";
                  const phone =
                    user?.individual?.phone_number || user?.phone_number;

                  return (
                    <motion.tr
                      key={entry.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.1 }}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "transparent" : colors.primary[800],
                        cursor: "pointer",
                      }}
                    >
                      <TableCell sx={{ fontSize: "0.85rem" }}>{role}</TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {fullName}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {phone || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {email}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCrewId(entry.id);
                            setOpenDialog(true);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Crew Button */}

        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            <IconButton
              onClick={onAddCrewClick}
              sx={{
                px: 2,
                py: 1,
                borderRadius: "10px",
                gap: 1,
                "@media print": {
                  display: "none",
                },
              }}
            >
              <AddCircleOutlineOutlined />
              <Typography variant="button">Add Crew Member</Typography>
            </IconButton>
          </motion.div>
        </AnimatePresence>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Crew Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this crew member from the call
            sheet?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CrewTable;
